import { ReactComponent as ShareIcon } from '@/assets/images/svg/share.svg';
import { getSocialShareLinks } from '@/utils';
import {
    Facebook as FacebookIcon,
    Telegram as TelegramIcon,
    X as XIcon,
} from '@mui/icons-material';
import { Box, IconButton, Menu, MenuItem, Stack } from '@mui/material';
import React, { useState } from 'react';
import styled from 'styled-components';

interface ShareMediaProps {
    className?: string;
    url: string;
    text: string;
    onClick?: () => void;
}

const StyledIconButton = styled.div`
    color: #000;
    background-color: #fff;
    border-radius: 10px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: #f5f5f5;
    }

    svg {
        width: 14px;
        height: 14px;
    }
`;

const StyledMenuItem = styled(MenuItem)`
    padding: 8px 16px;
    min-height: 40px;

    &:hover {
        background-color: rgba(0, 0, 0, 0.04);
    }
`;

const StyledMediaIcon = styled(IconButton)`
    &.x {
        svg {
            width: 18px;
            height: 18px;
        }
    }

    svg {
        width: 20px;
        height: 20px;
    }
`;

const ShareMedia: React.FC<ShareMediaProps> = ({
    className,
    url,
    text,
    onClick,
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget);
        if (onClick) {
            onClick();
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleShare = (platform: string, shareUrl: string) => {
        window.open(shareUrl, '_blank', 'noopener,noreferrer');
        handleClose();
    };

    const shareLinks = getSocialShareLinks(url, text);

    const open = Boolean(anchorEl);

    return (
        <Box className={className}>
            <StyledIconButton
                onClick={handleClick}
                aria-controls={open ? 'share-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
            >
                <ShareIcon />
            </StyledIconButton>

            <Menu
                id="share-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'share-button',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        minWidth: '200px',
                        mt: 1,
                    },
                }}
            >
                <StyledMenuItem
                    onClick={() => handleShare('twitter', shareLinks.twitter)}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <StyledMediaIcon
                            size="small"
                            className="x"
                            sx={{
                                color: '#000',
                                '&:hover': {
                                    backgroundColor: 'rgba(29, 161, 242, 0.1)',
                                },
                            }}
                        >
                            <XIcon fontSize="small" />
                        </StyledMediaIcon>
                        <span>Share on X</span>
                    </Stack>
                </StyledMenuItem>

                <StyledMenuItem
                    onClick={() => handleShare('telegram', shareLinks.telegram)}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <StyledMediaIcon
                            size="small"
                            sx={{
                                color: '#000',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 136, 204, 0.1)',
                                },
                            }}
                        >
                            <TelegramIcon fontSize="small" />
                        </StyledMediaIcon>
                        <span>Share on Telegram</span>
                    </Stack>
                </StyledMenuItem>

                <StyledMenuItem
                    onClick={() => handleShare('facebook', shareLinks.facebook)}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <StyledMediaIcon
                            size="small"
                            sx={{
                                color: '#000',
                                '&:hover': {
                                    backgroundColor: 'rgba(66, 103, 178, 0.1)',
                                },
                            }}
                        >
                            <FacebookIcon fontSize="small" />
                        </StyledMediaIcon>
                        <span>Share on Facebook</span>
                    </Stack>
                </StyledMenuItem>
            </Menu>
        </Box>
    );
};

export default ShareMedia;
